import useMediaQuery from '@material-ui/core/useMediaQuery'
import { graphql } from 'gatsby'
import React, { useMemo } from 'react'

import { SanityFeaturedProductsBannerFragment } from '../../../graphql/gatsby'
import { ParentProductSelectionForModal } from '../../../lib/products/modal/types'
import { useSanityRoleBasedContent } from '../../../lib/sanity'
import { FeaturedProductEntries } from '../FeaturedProductEntries'
import { FeaturedProductsSlider, SliderConfig } from '../FeaturedProductsSlider'
import * as Styled from './styled'

export type FeaturedProductsBannerProps = SanityFeaturedProductsBannerFragment & {
  className?: string
  config?: SliderConfig
}

const FeaturedProductsBanner: React.FC<FeaturedProductsBannerProps> = ({
  className,
  config,
  title,
  subtitle,
  products,
  roleBasedContent,
}) => {
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })
  const productsDetails = useMemo(
    () =>
      products?.reduce((acc: ParentProductSelectionForModal[], upsellProduct) => {
        const { sku, tagline, shortDescription, ...rest } = upsellProduct ?? {}
        if (sku) {
          acc.push({
            ...rest,
            sku,
            tagline: tagline ?? '',
            shortDescription: shortDescription ?? '',
          })
        }
        return acc
      }, []),
    [products],
  )

  const isMobile = useMediaQuery('(max-width:1080px)')

  if (!shouldDisplay || !productsDetails) {
    return null
  }

  return (
    <Styled.Background className={className}>
      <Styled.Container>
        <Styled.Title>{title ?? 'Featured Products'}</Styled.Title>
        {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}
        {!isMobile && <FeaturedProductsSlider productsDetails={productsDetails} config={config} />}
        {isMobile && (
          <Styled.ScrollableContainer>
            <FeaturedProductEntries productsDetails={productsDetails} />
          </Styled.ScrollableContainer>
        )}
      </Styled.Container>
    </Styled.Background>
  )
}

export const featuredProductsBannerFragment = graphql`
  fragment sanityFeaturedProductsBanner on SanityFeaturedProductsBanner {
    title
    subtitle
    roleBasedContent {
      ...RoleBasedContentFragment
    }
    products {
      ...sanityParentProduct
    }
  }
`

export default FeaturedProductsBanner
